import type { ListingLike } from "@/src/shared/types/listing";

type Nullable<T> = T | null | undefined;

interface IAnalyticsItemModel {
  item_id: string | number | null;
  item_name: string | null;
  category?: string | null;
  price?: number | null;
  [k: string]: unknown;
}

export interface IInitOptions {
  gtmId?: string;
  fbPixelId?: string;
}

export interface IPurchaseOptions {
  item?: ListingLike;
  value?: number | null;
  currency?: string;
  transaction_id?: string | null;
  params?: Record<string, unknown>;
}

// Global window extensions for analytics
declare global {
  interface Window {
    dataLayer?: unknown[];
    fbq?: (...args: unknown[]) => void;
    fbPixelActivated?: boolean;
    fbPixelInitialized?: boolean;
    __analytics?: {
      pushEvent?: (eventName: string, payload?: unknown) => void;
    };
  }
}

const isBrowser = typeof window !== "undefined";

const defaultModelForItem = (item: ListingLike): IAnalyticsItemModel => ({
  item_id: item?.id ?? null,
  item_name: item.title ?? null,
  category: item.place_type ?? null,
  // prefer explicit price fields used in Listing
  price: item.price ?? item.showable_price ?? null,
});

/** The GTM data layer, created when GTM (loaded in `app/layout.tsx`) has not made it yet. */
const ensureDataLayer = () => (window.dataLayer ??= []);

// Wait for dataLayer to be ready before pushing events
const waitForDataLayer = (): Promise<void> => {
  return new Promise((resolve) => {
    if (!isBrowser) {
      resolve();
      return;
    }

    ensureDataLayer();

    // If GTM is already loaded, resolve immediately
    if (window.dataLayer && window.dataLayer.length > 0) {
      resolve();
      return;
    }

    // Wait for GTM to initialize
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait
    const checkInterval = setInterval(() => {
      attempts++;
      if (
        (window.dataLayer && window.dataLayer.length > 0) ||
        attempts >= maxAttempts
      ) {
        clearInterval(checkInterval);
        resolve();
      }
    }, 100);
  });
};

// export so other runtime scripts (inline or non-ESM) can import/call if needed
export const pushToDataLayer = async (
  eventName: string,
  payload: Record<string, unknown> = {},
): Promise<void> => {
  if (!isBrowser) return;

  try {
    await waitForDataLayer();
    const dataLayer = ensureDataLayer();

    const pushObj = {
      event: eventName,
      timestamp: new Date().toISOString(),
      // No query string: a `?token=` sign-in link must never reach the analytics vendors.
      page_url: `${window.location.origin}${window.location.pathname}`,
      page_path: window.location.pathname,
      ...payload,
    };

    dataLayer.push(pushObj);
  } catch (_e) {}
};

function init({ gtmId, fbPixelId }: IInitOptions = {}) {
  if (!isBrowser) return;
  ensureDataLayer();
  void pushToDataLayer("analytics_init", {
    gtmId: gtmId ?? null,
    fbPixelId: fbPixelId ?? null,
  });

  window.__analytics = window.__analytics || {};
  window.__analytics.pushEvent = (eventName, payload) =>
    void pushToDataLayer(eventName, payload as Record<string, unknown>);
}

function track(eventName: string, payload: Record<string, unknown> = {}) {
  void pushToDataLayer(eventName, payload);
}

function signin(user?: { phone?: string | number; method?: string }) {
  track("signin", {
    method: user?.method ?? "unknown",
    phone: user?.phone ?? null,
  });
}

function signup(user?: {
  phone?: string | number;
  method?: string;
  name?: string;
}) {
  track("signup", {
    method: user?.method ?? "unknown",
    phone: user?.phone ?? null,
    name: user?.name ?? null,
  });
}

function view_item_list({
  items = [],
  list_name = "search_results",
  params = {},
}: {
  items?: ListingLike[];
  list_name?: string;
  params?: Record<string, unknown>;
} = {}) {
  const mapped = (items || []).map(defaultModelForItem);
  track("view_item_list", { items: mapped, list_name, ...params });
}

function view_item({
  item,
  params = {},
}: {
  item: ListingLike;
  params?: Record<string, unknown>;
}) {
  const mappedItem = defaultModelForItem(item);
  // single listing view - still wrap in items array for consistency
  track("view_item", { items: [mappedItem], ...params });
}

function add_to_cart({
  item,
  value = null,
  currency = "BDT",
  params = {},
}: {
  item: ListingLike;
  value?: Nullable<number>;
  currency?: string;
  params?: Record<string, unknown>;
}) {
  const mappedItem = defaultModelForItem(item);
  track("add_to_cart", { items: [mappedItem], value, currency, ...params });
}

function begin_checkout({
  item,
  value = null,
  currency = "BDT",
  params = {},
}: {
  item?: ListingLike;
  value?: Nullable<number>;
  currency?: string;
  params?: Record<string, unknown>;
} = {}) {
  const mapped = item ? [defaultModelForItem(item)] : [];
  track("begin_checkout", { items: mapped, value, currency, ...params });
}

function purchase({
  item,
  value = null,
  currency = "BDT",
  transaction_id = null,
  params = {},
}: IPurchaseOptions) {
  const mapped = item ? [defaultModelForItem(item)] : [];
  track("purchase", {
    items: mapped,
    value,
    currency,
    transaction_id,
    ...params,
  });
}

function customAction(
  actionName: string,
  payload: Record<string, unknown> = {},
) {
  track(actionName, payload);
}

function page_view({
  page_title = "",
  page_location = "",
  params = {},
}: {
  page_title?: string;
  page_location?: string;
  params?: Record<string, unknown>;
} = {}) {
  track("page_view", { page_title, page_location, ...params });
}

const analytics = {
  init,
  track,
  signin,
  signup,
  view_item_list,
  view_item,
  add_to_cart,
  begin_checkout,
  purchase,
  customAction,
  page_view,
};

export default analytics;
